import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase, SUPABASE_URL } from '@/integrations/supabase/client';
import { useIntegrations } from '@/hooks/useIntegrations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { Loader2, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface StripeIntegrationSectionProps {
  communityId: string;
}

interface StripeCredentials {
  public_key: string | null;
  secret_key: string | null;
  webhook_secret: string | null;
  is_active?: boolean;
}

const StripeIntegrationSection: React.FC<StripeIntegrationSectionProps> = ({ communityId }) => {
  const { integrations, isStripeActive, refetch: refetchIntegrations } = useIntegrations();
  const [publicKey, setPublicKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isCredentialsOpen, setIsCredentialsOpen] = useState(false);

  // Fetch existing Stripe integration
  const { data: integration, isLoading, error, refetch } = useQuery({
    queryKey: ['stripe-integration-details', communityId],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.rpc('get_community_stripe_credentials', {
          p_community_id: communityId
        });
        
        if (error) throw error;
        
        return data as unknown as StripeCredentials;
      } catch (error) {
        console.error('Error fetching stripe credentials:', error);
        throw error;
      }
    },
    enabled: !!communityId
  });

  // Set form values from fetched data
  useEffect(() => {
    if (integration) {
      setPublicKey(integration.public_key || '');
      // Load actual credential values so they can be toggled for visibility
      setSecretKey(integration.secret_key || '');
      setWebhookSecret(integration.webhook_secret || '');
      setIsActive(integration.is_active || false);
    }
  }, [integration]);

  // Immediately save the active status when the switch is toggled
  const saveActiveStatus = async (active: boolean) => {
    try {
      // Check if integration exists
      const { data: existingIntegration } = await supabase
        .from('community_integrations')
        .select('id')
        .eq('community_id', communityId)
        .eq('integration_type', 'stripe')
        .maybeSingle();

      if (existingIntegration) {
        // Update existing integration with just is_active
        const { error } = await supabase
          .from('community_integrations')
          .update({
            is_active: active,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingIntegration.id);

        if (error) throw error;
      } else {
        // Create new integration with minimal required fields
        const { error } = await supabase
          .from('community_integrations')
          .insert({
            community_id: communityId,
            integration_type: 'stripe',
            config: { public_key: null, secret_key: null, webhook_secret: null },
            is_active: active
          });

        if (error) throw error;
      }

      toast({
        title: active ? "Stripe Integration Enabled" : "Stripe Integration Disabled",
        description: active 
          ? "Please configure your Stripe credentials below." 
          : "Stripe integration has been disabled.",
      });
      
      // Refetch data
      refetch();
      refetchIntegrations();
      
    } catch (error) {
      console.error('Error saving Stripe integration active status:', error);
      toast({
        title: "Error",
        description: `Failed to update Stripe integration status: ${(error as Error).message}`,
        variant: "destructive"
      });
      // Revert the UI state if there was an error
      setIsActive(!active);
    }
  };

  // Handle switch toggle
  const handleActivationToggle = async (checked: boolean) => {
    setIsActive(checked);
    await saveActiveStatus(checked);
  };

  // Save integration settings
  const saveSettings = async () => {
    try {
      // Check if we have the required keys
      if (!publicKey) {
        toast({
          title: "Validation Error",
          description: "Please provide a public key",
          variant: "destructive"
        });
        return;
      }

      // Check if integration exists
      const { data: existingIntegration } = await supabase
        .from('community_integrations')
        .select('id')
        .eq('community_id', communityId)
        .eq('integration_type', 'stripe')
        .maybeSingle();

      // Prepare config object
      const config: Record<string, string> = {
        public_key: publicKey,
      };
      
      // Only update secret key if a new value was provided
      if (secretKey.trim()) {
        config.secret_key = secretKey;
      } else if (integration?.secret_key) {
        // Keep existing secret key if no new value provided
        config.secret_key = integration.secret_key;
      }
      
      // Only update webhook secret if a new value was provided
      if (webhookSecret.trim()) {
        config.webhook_secret = webhookSecret;
      } else if (integration?.webhook_secret) {
        // Keep existing webhook secret if no new value provided
        config.webhook_secret = integration.webhook_secret;
      }

      if (existingIntegration) {
        // Update existing integration
        const { error } = await supabase
          .from('community_integrations')
          .update({
            config,
            is_active: isActive,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingIntegration.id);

        if (error) throw error;
      } else {
        // Create new integration
        const { error } = await supabase
          .from('community_integrations')
          .insert({
            community_id: communityId,
            integration_type: 'stripe',
            config,
            is_active: isActive
          });

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: "Stripe integration settings saved successfully",
      });
      
      // Refetch data
      refetch();
      refetchIntegrations();
      
    } catch (error) {
      console.error('Error saving Stripe integration:', error);
      toast({
        title: "Error",
        description: `Failed to save Stripe settings: ${(error as Error).message}`,
        variant: "destructive"
      });
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={`${SUPABASE_URL}/storage/v1/object/public/shared-assets/stripe-logo.jpeg`}
              alt="Stripe Logo" 
              className="w-8 h-8 object-contain"
            />
            <div>
              <CardTitle className="text-xl font-semibold">Stripe Integration</CardTitle>
              <CardDescription className="mt-1">
                Configure Stripe payment processing for subscriptions and other payments.
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge variant={isActive ? "default" : "secondary"} className="text-xs">
              {isActive ? "Active" : "Inactive"}
            </Badge>
            <a
              href="https://dashboard.stripe.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Stripe Dashboard
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex items-center space-x-2">
              <Switch 
                id="stripe-active"
                checked={isActive}
                onCheckedChange={handleActivationToggle}
              />
              <Label htmlFor="stripe-active" className="text-sm font-medium">
                Enable Stripe integration
              </Label>
            </div>
            
            {isActive && (
              <div className="animate-in fade-in slide-in-from-top-5 duration-300">
                <Collapsible open={isCredentialsOpen} onOpenChange={setIsCredentialsOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" className="w-full justify-between hover:bg-muted/50">
                      <span className="font-medium">Configure Stripe Credentials</span>
                      {isCredentialsOpen ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-4">
                    <div className="bg-muted/30 border rounded-lg p-6 space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-sm text-muted-foreground">
                          Configure your Stripe API keys to enable payment processing.
                        </p>
                        <a
                          href="https://dashboard.stripe.com/apikeys"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          Get API Keys
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="grid gap-2">
                          <Label htmlFor="publicKey" className="text-sm font-medium">
                            Publishable Key
                          </Label>
                          <Input
                            id="publicKey"
                            value={publicKey}
                            onChange={(e) => setPublicKey(e.target.value)}
                            placeholder="pk_test_..."
                            className="font-mono text-sm"
                          />
                        </div>
                        
                        <div className="grid gap-2">
                          <Label htmlFor="secretKey" className="text-sm font-medium">
                            Secret Key
                          </Label>
                          <PasswordInput
                            id="secretKey"
                            value={secretKey}
                            onChange={(e) => setSecretKey(e.target.value)}
                            placeholder="sk_test_..."
                            className="font-mono text-sm"
                          />
                        </div>
                        
                        <div className="grid gap-2">
                          <Label htmlFor="webhookSecret" className="text-sm font-medium">
                            Webhook Secret
                          </Label>
                          <PasswordInput
                            id="webhookSecret"
                            value={webhookSecret}
                            onChange={(e) => setWebhookSecret(e.target.value)}
                            placeholder="whsec_..."
                            className="font-mono text-sm"
                          />
                          <p className="text-xs text-muted-foreground">
                            Used to verify Stripe webhook events. Find this in the{" "}
                            <a
                              href="https://dashboard.stripe.com/webhooks"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              Stripe Dashboard → Webhooks
                            </a>
                          </p>
                        </div>
                      </div>
                      
                      <div className="pt-4 border-t">
                        <Button onClick={saveSettings} className="w-full">
                          Save Credentials
                        </Button>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StripeIntegrationSection;
